import { consumeSse } from "@/lib/sse";
import { ApiError } from "@/lib/api";
import { parseFilenameFromHeader } from "@/lib/download";
import type { PaaExtraction } from "../types";
import type { CombineMove, CombinePageRef, CombinePrepared } from "./types";

// POST /api/paa/combine/prepare — read the approved set's sheet numbers and
// propose the combined order.
//
// Streamed, and not for show: reading a 60-sheet approved set is tens of seconds
// of PdfPig, and a request that carries no bytes for that long is one an idle
// proxy is entitled to close — which is what a "network error" on a 22-sheet
// comparison turned out to be. SseWriter's keep-alive covers the silence.
//
// `compareId` is the fast path: when a comparison is still live on the server it
// already holds both plan sets, so combining straight after a review uploads
// nothing at all. An expired id is not an error — the files are sent instead.
export async function preparePaaCombine(
  data: PaaExtraction,
  opts: {
    approvedFile?: File | null;
    updatedFile?: File | null;
    compareId?: string | null;
    onProgress: (msg: string) => void;
    signal?: AbortSignal;
    sessionId?: string | null;
  },
): Promise<CombinePrepared> {
  const form = new FormData();
  form.append("data", JSON.stringify(data));
  if (opts.compareId) {
    form.append("compareId", opts.compareId);
  }
  // Sent alongside the id, not instead of it: the server uses the id when it can
  // and the files when it cannot, and the client cannot know which it will be.
  if (opts.approvedFile) form.append("approvedFile", opts.approvedFile);
  if (opts.updatedFile) form.append("updatedFile", opts.updatedFile);
  if (opts.sessionId) form.append("sessionId", opts.sessionId);

  const res = await fetch("/api/paa/combine/prepare", {
    method: "POST",
    credentials: "same-origin",
    body: form,
    signal: opts.signal,
  });

  return new Promise<CombinePrepared>((resolve, reject) => {
    consumeSse<CombinePrepared>(res, {
      onProgress: opts.onProgress,
      onResult: resolve,
      onError: (msg) => reject(new Error(msg)),
    }).catch(reject);
  });
}

// POST /api/paa/combine/build — the combined set, in the order the operator
// approved. The server never re-derives that order: what they saw is what is
// built.
//
// Both files ride along when we have them. The reason is not caching: losing
// somebody's reordering because the server let go of two byte arrays while they
// were reading would be indefensible, so an expired session costs an upload
// rather than the review.
export async function buildPaaCombine(
  pages: CombinePageRef[],
  data: PaaExtraction,
  opts: {
    combineId?: string | null;
    approvedFile?: File | null;
    updatedFile?: File | null;
    moves?: CombineMove[];
    sessionId?: string | null;
    signal?: AbortSignal;
  },
): Promise<{ blob: Blob; filename: string }> {
  const form = new FormData();
  form.append("pages", JSON.stringify(pages));
  form.append("data", JSON.stringify(data));
  if (opts.combineId) form.append("combineId", opts.combineId);
  if (opts.approvedFile) form.append("approvedFile", opts.approvedFile);
  if (opts.updatedFile) form.append("updatedFile", opts.updatedFile);
  if (opts.moves?.length) form.append("moves", JSON.stringify(opts.moves));
  if (opts.sessionId) form.append("sessionId", opts.sessionId);

  const res = await fetch("/api/paa/combine/build", {
    method: "POST",
    credentials: "same-origin",
    body: form,
    signal: opts.signal,
  });

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* swallow */
    }
    throw new ApiError(res.status, body);
  }

  const blob = await res.blob();
  const filename =
    parseFilenameFromHeader(res.headers.get("content-disposition")) ??
    "combined-plan-set.pdf";
  return { blob, filename };
}

// GET /api/paa/combine/page-image — one page of either set, rendered.
//
// A URL rather than a fetch, like sheetImageUrl: as an <img src> the browser
// handles loading, caching and cancellation, and a 60-row list scrolled up and
// down does not re-download a thing.
export function combinePageImageUrl(
  combineId: string,
  set: "approved" | "updated",
  page: number,
): string {
  // No dpi. It used to send 110, which the server read as "fit this page into a
  // US-Letter box at 110 DPI" — so a 24x36 plan sheet arrived 1210px wide, about
  // 33 pixels per inch of drawing, and the title-block text the operator is here
  // to read was four pixels tall with nothing behind it to zoom into. The server
  // now owns the pixel budget (PaaController.ViewerLongEdgePx), which is the
  // right place for it: it is the thing that knows what a render costs.
  const params = new URLSearchParams({ combineId, set, page: String(page) });
  return `/api/paa/combine/page-image?${params}`;
}

// DELETE /api/paa/combine — "done with this, stop holding both plan sets."
//
// Fire and forget, for the same reasons as releasePaaCompare: two plan sets are
// hundreds of megabytes on a 4 GB box, nothing depends on the result, and the
// idle timer is the backstop. `keepalive` so it survives the navigation that
// usually triggers it.
export function releasePaaCombine(combineId: string | null | undefined): void {
  if (!combineId) return;
  void fetch(`/api/paa/combine?combineId=${encodeURIComponent(combineId)}`, {
    method: "DELETE",
    credentials: "same-origin",
    keepalive: true,
  }).catch(() => {
    /* the idle timer is the backstop */
  });
}
